import { imageQualityForSource } from '../../../image-loader';
import {
  Component,
  ViewEncapsulation,
  input,
  inject,
  computed,
  effect,
  PLATFORM_ID,
  RESPONSE_INIT,
  isDevMode,
} from '@angular/core';
import { DOCUMENT, NgOptimizedImage, isPlatformServer } from '@angular/common';
import { ButtonVariant } from '../../atoms/button/button';
import { StatsBlockComponent, StatItem } from '../stats-block/stats-block';

export type HeroSectionVariant = '' | 'image';

export type HeroH1Source = 'overline' | 'heading';

export interface HeroSectionAction {
  label: string;
  href: string;
  variant: ButtonVariant;
}

/**
 * Vercel `/_vercel/image` allowed widths — keep in sync with
 * `src/app/image-loader.ts` `VERCEL_WIDTHS`. We do NOT import that constant
 * here because the loader has dev-mode + SVG branching that returns the raw
 * src — the component's Link-header logic mirrors loader semantics rather
 * than reusing the function (the loader runs per-render-pass on every <img>
 * <picture> source it encounters; we're emitting ONE preload entry).
 *
 * quick-96: HTTP Link header for hero image preload.
 */
const VERCEL_WIDTHS = [384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840] as const;

function nearestAllowedWidth(width: number): number {
  for (const allowed of VERCEL_WIDTHS) {
    if (allowed >= width) return allowed;
  }
  return VERCEL_WIDTHS[VERCEL_WIDTHS.length - 1];
}

@Component({
  selector: 'ds-hero-section',
  templateUrl: './hero-section.html',
  styleUrl: './hero-section.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [StatsBlockComponent, NgOptimizedImage],
})
export class HeroSectionComponent {
  readonly sectionId = input<string>('');
  readonly variant = input<HeroSectionVariant>('');
  readonly overline = input('');
  readonly heading = input.required<string>();
  readonly h1Source = input<HeroH1Source>('overline');
  readonly subtitle = input('');
  readonly lead = input('');
  readonly backgroundImage = input('');
  readonly backgroundImageWidth = input<number>(0);
  readonly backgroundImageHeight = input<number>(0);
  readonly backgroundImageAlt = input<string>('');
  readonly actions = input<HeroSectionAction[]>([]);
  readonly stats = input<StatItem[]>([]);
  readonly cssClass = input<string>('');

  readonly imageSrcset = computed(() => /\.svg(\?|$)/i.test(this.backgroundImage())
    ? '' : VERCEL_WIDTHS.map(width => `${width}w`).join(', '));

  readonly imageSizes = computed(() => this.cssClass().split(' ').includes('agency-live-hero')
    ? '(max-width: 767px) calc(100vw - 32px), (min-width: 1120px) 496px, calc((100vw - 80px) / 2.1)'
    : '100vw');

  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });

  /** Resolve hash-only hrefs against current path so <base href="/"> doesn't break them */
  readonly resolvedActions = computed(() =>
    this.actions().map(a => ({
      ...a,
      href: a.href.startsWith('#')
        ? (this.doc.location?.pathname ?? '/') + a.href
        : a.href,
    })),
  );

  /**
   * Spec 31: which tag the overline slot emits.
   * - `null`  → no overline element rendered (overline string is empty)
   * - `'h1'`  → h1Source='overline' (default), or h1Source='heading' but heading is empty
   * - `'p'`   → h1Source='heading' and heading is present
   */
  readonly overlineElement = computed<'h1' | 'p' | null>(() => {
    const hasOverline = this.overline().trim().length > 0;
    const hasHeading = this.heading().trim().length > 0;
    if (!hasOverline) return null;
    if (this.h1Source() === 'overline') return 'h1';
    // h1Source='heading': overline demotes to p, unless heading itself is empty
    return hasHeading ? 'p' : 'h1';
  });

  /**
   * Spec 31: which tag the heading slot emits.
   * - `null`  → no heading element rendered (heading string is empty)
   * - `'h1'`  → h1Source='heading', or h1Source='overline' but overline is empty
   * - `'h2'`  → h1Source='overline' (default) and overline is present
   */
  readonly headingElement = computed<'h1' | 'h2' | null>(() => {
    const hasOverline = this.overline().trim().length > 0;
    const hasHeading = this.heading().trim().length > 0;
    if (!hasHeading) return null;
    if (this.h1Source() === 'heading') return 'h1';
    // h1Source='overline': heading stays h2, unless overline itself is empty
    return hasOverline ? 'h2' : 'h1';
  });

  constructor() {
    /**
     * quick-96: emit an HTTP `Link: <hero>; rel=preload; as=image` response
     * header during SSR so the browser's preload scanner sees the hero
     * preload BEFORE parsing the ~45 KiB SSR HTML head. Closes the 1,930 ms
     * Lighthouse "Resource Load Delay" measured on home post-quick-95.
     *
     * Layered on top of NgOptimizedImage's in-body `<link rel="preload">`
     * tag — the in-body tag stays for browsers that don't process Link
     * headers and as a srcset fallback. The HTTP header is ADDITIVE.
     *
     * Mirrors the merge contract of quick-92's styles Link header (set on
     * Express `res` directly in `src/server.ts`). HTTP allows multiple
     * comma-separated Link entries; @angular/ssr's
     * `writeResponseToNodeResponse` clobbers `res.setHeader('link', ...)`
     * with whatever it finds in `responseInit.headers`, so this code path
     * uses `Headers#append` (not `set`) so RFC 8288 multi-value semantics
     * are preserved if any other component / future middleware adds Link
     * entries to the same SSR Response.
     *
     * The effect is server-only: `inject(RESPONSE_INIT, { optional: true })`
     * returns `null` on the browser (Angular SSR only provides the token in
     * RenderMode.Server platform providers). The `isPlatformServer` guard
     * is defence-in-depth.
     */
    effect(() => {
      if (!isPlatformServer(this.platformId)) return;
      if (!this.responseInit) return;

      const src = this.backgroundImage();
      if (!src) return; // No hero image (variant='' or empty backgroundImage). No-op.
      if (this.variant() !== 'image') return; // Variant='' suppresses the <img>; suppress preload too.

      const href = this.heroOptimisedHref(src, this.backgroundImageWidth() || 1920);
      const isSvg = /\.svg(\?|$)/i.test(src);

      // Build the comma-joined imagesrcset matching what NgOptimizedImage
      // emits on the in-body <link> tag (same widths, same quality). Skipped
      // for SVGs since they bypass /_vercel/image and a srcset is moot.
      const linkValue = isSvg
        ? `<${href}>; rel=preload; as=image; fetchpriority=high`
        : `<${href}>; rel=preload; as=image; fetchpriority=high; ` +
          `imagesizes="${this.imageSizes()}"; imagesrcset="${this.heroSrcset(src)}"`;

      // ResponseInit.headers is a Headers instance constructed by @angular/ssr
      // (verified at preflight step 2). Use Headers#append to coexist with any
      // future component-emitted Link entry. We do NOT clobber an existing
      // value — Headers.append is multi-value-safe and writeResponseToNodeResponse
      // forwards comma-joined values to res.setHeader.
      const headers = this.responseInit.headers as Headers | undefined;
      if (headers && typeof (headers as Headers).append === 'function') {
        (headers as Headers).append('Link', linkValue);
      } else {
        // Defensive fallback if @angular/ssr ever switches to a plain object
        // for headers (currently a Headers instance per preflight step 2).
        // We coerce to a tuple-array shape that ResponseInit accepts.
        const ri = this.responseInit as ResponseInit & { headers?: HeadersInit };
        const existing = ri.headers;
        if (!existing) {
          ri.headers = { Link: linkValue };
        } else if (Array.isArray(existing)) {
          existing.push(['Link', linkValue]);
        } else if (existing instanceof Headers) {
          existing.append('Link', linkValue);
        } else {
          // Plain Record<string,string> — promote to array to allow multi-value.
          const entries: [string, string][] = Object.entries(existing as Record<string, string>);
          entries.push(['Link', linkValue]);
          ri.headers = entries;
        }
      }
    });
  }

  /**
   * Mirror of `vercelImageLoader` (src/app/image-loader.ts) without the
   * `ImageLoaderConfig` indirection. Returns the URL the browser will
   * actually fetch for the hero `<img>`:
   *  - SVG → raw src (Vercel optimiser bypassed)
   *  - dev mode → raw src (loader returns early on isDevMode())
   *  - production raster → /_vercel/image?url=<src>&w=<nearest>&q=<quality>
   *
   * Kept private to the component so the Link header value cannot drift
   * from the optimiser the IMG tag actually uses.
   */
  private heroOptimisedHref(src: string, width: number): string {
    if (/\.svg(\?|$)/i.test(src)) return src;
    if (isDevMode()) return src;
    const w = nearestAllowedWidth(width);
    return `/_vercel/image?url=${encodeURIComponent(src)}&w=${w}&q=${imageQualityForSource(src)}`;
  }

  /**
   * Build the comma-joined `imagesrcset` string covering every Vercel
   * allowed width. Mirrors what NgOptimizedImage emits on the in-body
   * <link rel="preload"> tag for the same hero image.
   */
  private heroSrcset(src: string): string {
    if (isDevMode()) {
      // Dev mode: no /_vercel/image rewriting; srcset is moot. Return
      // a single-entry srcset pointing at the raw src so the header is
      // still well-formed.
      return `${src} 1x`;
    }
    const encoded = encodeURIComponent(src);
    return VERCEL_WIDTHS
      .map((w) => `/_vercel/image?url=${encoded}&w=${w}&q=${imageQualityForSource(src)} ${w}w`)
      .join(', ');
  }
}
